import { Bot, Sparkles, Play, CheckCircle2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface DeepAgentContextProps {
    isResearching: boolean;
    researchResult: string | null;
    activeLogs?: string[];
    onRunDeepScan: () => void;
}

export function DeepAgentContext({
    isResearching,
    researchResult,
    activeLogs = [],
    onRunDeepScan
}: DeepAgentContextProps) {
    return (
        <div className="relative group">
            {/* Integrated "Card" look without hard borders */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl" />

            <div className="relative bg-[#0A0A0A]/50 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-white/5 shadow-2xl overflow-hidden">
                {/* Ambient Glow */}
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[100px] -mr-40 -mt-40 pointer-events-none" />

                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                            <Bot className="w-6 h-6 text-blue-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">Deep Agent Context</h2>
                    </div>

                    {!isResearching && !researchResult ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <div className="w-16 h-16 bg-[#111] rounded-2xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-105 transition-transform duration-500 border border-white/5">
                                <Sparkles className="w-8 h-8 text-blue-500" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">Await Intelligence Command</h3>
                            <p className="text-gray-500 max-w-lg text-sm mb-8 leading-relaxed">
                                Initialize the Deep Research Agent to scan federal databases, news sources, and competitor filings.
                            </p>
                            <button
                                onClick={onRunDeepScan}
                                className="px-8 py-3 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-colors flex items-center gap-2 shadow-[0_0_30px_rgba(255,255,255,0.15)]"
                            >
                                <Play className="w-4 h-4 fill-current" />
                                RUN DEEP SCAN
                            </button>
                        </div>
                    ) : isResearching ? (
                        <div className="py-8">
                            <div className="flex flex-col items-center justify-center mb-10 text-center">
                                <div className="relative w-24 h-24 mb-6">
                                    <div className="absolute inset-0 border-2 border-blue-500/20 rounded-full" />
                                    <div className="absolute inset-0 border-2 border-t-blue-500 rounded-full animate-spin" />
                                    <Bot className="absolute inset-0 m-auto w-8 h-8 text-blue-400 animate-pulse" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">Deep Agent Orchestrating</h3>
                                <p className="text-blue-400 text-sm font-mono tracking-wider animate-pulse uppercase">Synchronizing Expert Insights...</p>
                            </div>

                            <div className="bg-black/60 rounded-2xl p-6 border border-white/5 font-mono text-[11px] space-y-2 max-h-[250px] overflow-y-auto scrollbar-hide">
                                {activeLogs.map((log, i) => (
                                    <div key={i} className="flex gap-3 text-gray-400 fade-in border-l border-blue-500/30 pl-3 py-0.5">
                                        <span className="text-blue-500/50">[{new Date().toLocaleTimeString([], { hour12: false })}]</span>
                                        <span className={i === activeLogs.length - 1 ? 'text-white' : ''}>{log}</span>
                                    </div>
                                ))}
                                <div className="flex gap-3 text-blue-400 animate-pulse pl-3 border-l border-blue-500/30">
                                    <span className="opacity-50">[{new Date().toLocaleTimeString([], { hour12: false })}]</span>
                                    <span>_</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="prose prose-invert prose-blue max-w-none">
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/5">
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                                <span className="text-green-400 font-mono text-xs tracking-widest uppercase">Analysis Complete</span>
                            </div>
                            <div className="text-gray-300 leading-relaxed font-light text-base">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {researchResult || ''}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
